import * as React from "react"
import { cva } from "class-variance-authority"

import { cn } from "../../lib/utils"

/**
 * Badge Component - COULEURS FONCÉES AUTOMATIQUES
 * ===============================================
 * Ce composant garantit que TOUS les badges ont des couleurs foncées
 * avec texte blanc pour une meilleure lisibilité.
 * 
 * Les classes claires (bg-*-100, text-*-800) sont automatiquement
 * converties en couleurs foncées.
 */

// Mapping des couleurs claires vers chips DÉGRADÉS (conversion automatique)
const g = (from, to) => `bg-gradient-to-r from-${from} to-${to}`;
const LIGHT_TO_DARK = {
  'bg-red-100': g('red-500', 'rose-600'), 'bg-red-200': g('red-500', 'rose-600'),
  'bg-blue-100': g('blue-500', 'indigo-600'), 'bg-blue-200': g('blue-500', 'indigo-600'),
  'bg-green-100': g('emerald-500', 'green-600'), 'bg-green-200': g('emerald-500', 'green-600'),
  'bg-yellow-100': g('amber-400', 'yellow-500'), 'bg-yellow-200': g('amber-400', 'yellow-500'),
  'bg-amber-100': g('amber-500', 'orange-500'), 'bg-amber-200': g('amber-500', 'orange-500'),
  'bg-orange-100': g('orange-500', 'amber-600'), 'bg-orange-200': g('orange-500', 'amber-600'),
  'bg-purple-100': g('purple-500', 'fuchsia-600'), 'bg-purple-200': g('purple-500', 'fuchsia-600'),
  'bg-pink-100': g('pink-500', 'rose-600'), 'bg-pink-200': g('pink-500', 'rose-600'),
  'bg-teal-100': g('teal-500', 'cyan-600'), 'bg-teal-200': g('teal-500', 'cyan-600'),
  'bg-cyan-100': g('cyan-500', 'sky-600'), 'bg-cyan-200': g('cyan-500', 'sky-600'),
  'bg-indigo-100': g('indigo-500', 'violet-600'), 'bg-indigo-200': g('indigo-500', 'violet-600'),
  'bg-emerald-100': g('emerald-500', 'green-600'), 'bg-emerald-200': g('emerald-500', 'green-600'),
  'bg-violet-100': g('violet-500', 'purple-600'), 'bg-violet-200': g('violet-500', 'purple-600'),
  'bg-gray-100': g('slate-500', 'gray-600'), 'bg-gray-200': g('slate-500', 'gray-600'),
  'bg-slate-100': g('slate-500', 'gray-600'), 'bg-slate-200': g('slate-500', 'gray-600'),
};

// Textes à convertir en blanc
const TEXT_TO_WHITE = [
  'text-red-800', 'text-red-700', 'text-red-900',
  'text-blue-800', 'text-blue-700', 'text-blue-900',
  'text-green-800', 'text-green-700', 'text-green-900',
  'text-yellow-800', 'text-yellow-700', 'text-yellow-900',
  'text-amber-800', 'text-amber-700', 'text-amber-900',
  'text-orange-800', 'text-orange-700', 'text-orange-900',
  'text-purple-800', 'text-purple-700', 'text-purple-900',
  'text-pink-800', 'text-pink-700', 'text-pink-900',
  'text-teal-800', 'text-teal-700', 'text-teal-900',
  'text-cyan-800', 'text-cyan-700', 'text-cyan-900',
  'text-indigo-800', 'text-indigo-700', 'text-indigo-900',
  'text-emerald-800', 'text-emerald-700', 'text-emerald-900',
  'text-violet-800', 'text-violet-700', 'text-violet-900',
  'text-gray-800', 'text-gray-700', 'text-gray-900',
  'text-slate-800', 'text-slate-700', 'text-slate-900',
];

/**
 * Convertit automatiquement les classes claires en foncées
 */
function autoDarkClasses(className) {
  if (!className) return '';
  
  let result = className;
  
  // Convertir les backgrounds clairs en foncés
  Object.entries(LIGHT_TO_DARK).forEach(([light, dark]) => {
    result = result.replace(new RegExp(`\\b${light}\\b`, 'g'), dark);
  });
  
  // Convertir les textes foncés en blanc
  TEXT_TO_WHITE.forEach((textClass) => {
    result = result.replace(new RegExp(`\\b${textClass}\\b`, 'g'), 'text-white');
  });
  
  // Si un bg-*-600 ou un dégradé est présent mais pas de text-white, l'ajouter
  if ((/bg-\w+-[5-9]00/.test(result) || result.includes('bg-gradient')) && !result.includes('text-white')) {
    result += ' text-white';
  }
  // Les chips dégradés reçoivent une ombre + un anneau interne subtil
  if (result.includes('bg-gradient')) {
    result += ' shadow-sm ring-1 ring-inset ring-white/15';
  }
  
  return result;
}

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shadow-sm ring-1 ring-inset ring-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Chips DÉGRADÉS avec texte BLANC pour une meilleure lisibilité
        default: "border-transparent bg-gradient-to-r from-slate-600 to-slate-700 text-white",
        secondary: "border-transparent bg-gradient-to-r from-slate-500 to-slate-600 text-white",
        destructive: "border-transparent bg-gradient-to-r from-red-500 to-rose-600 text-white",
        outline: "border-slate-400 bg-gradient-to-r from-slate-500 to-slate-600 text-white",
        success: "border-transparent bg-gradient-to-r from-emerald-500 to-green-600 text-white",
        warning: "border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-white",
        info: "border-transparent bg-gradient-to-r from-blue-500 to-indigo-600 text-white",
        purple: "border-transparent bg-gradient-to-r from-purple-500 to-fuchsia-600 text-white",
        orange: "border-transparent bg-gradient-to-r from-orange-500 to-amber-600 text-white",
        pink: "border-transparent bg-gradient-to-r from-pink-500 to-rose-600 text-white",
        cyan: "border-transparent bg-gradient-to-r from-cyan-500 to-sky-600 text-white",
        emerald: "border-transparent bg-gradient-to-r from-emerald-500 to-green-600 text-white",
        indigo: "border-transparent bg-gradient-to-r from-indigo-500 to-violet-600 text-white",
        teal: "border-transparent bg-gradient-to-r from-teal-500 to-cyan-600 text-white",
        violet: "border-transparent bg-gradient-to-r from-violet-500 to-purple-600 text-white",
        red: "border-transparent bg-gradient-to-r from-red-500 to-rose-600 text-white",
        blue: "border-transparent bg-gradient-to-r from-blue-500 to-indigo-600 text-white",
        green: "border-transparent bg-gradient-to-r from-emerald-500 to-green-600 text-white",
        yellow: "border-transparent bg-gradient-to-r from-amber-400 to-yellow-500 text-white",
        amber: "border-transparent bg-gradient-to-r from-amber-500 to-orange-500 text-white",
        gray: "border-transparent bg-gradient-to-r from-slate-500 to-gray-600 text-white",
        // Statuts
        inactive: "border-transparent bg-gradient-to-r from-slate-400 to-gray-500 text-white",
        active: "border-transparent bg-gradient-to-r from-emerald-500 to-green-600 text-white",
        pending: "border-transparent bg-gradient-to-r from-amber-400 to-yellow-500 text-white",
        // Transactions
        achat: "border-transparent bg-gradient-to-r from-blue-500 to-indigo-600 text-white",
        consommation: "border-transparent bg-gradient-to-r from-red-500 to-rose-600 text-white",
        bonus: "border-transparent bg-gradient-to-r from-emerald-500 to-green-600 text-white",
        expiration: "border-transparent bg-gradient-to-r from-orange-500 to-amber-600 text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  ...props
}) {
  // Convertir automatiquement les classes claires en foncées
  const darkClassName = autoDarkClasses(className);
  
  return (
    <div 
      className={cn(badgeVariants({ variant }), darkClassName)} 
      {...props} 
    />
  );
}

export { Badge, badgeVariants, autoDarkClasses }